import React, { useRef, useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { removeService, removePhoto, addPhoto, removeServiceComplementaire, removeRealisation } from "../../redux/actions/serviceActions";
import "./styles.css";
import FormServiceModal from "../FormServiceModal";
import FormRealisationModal from "../FormRealisationModal";
import CarouselControls from "../CarouselControls";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@fortawesome/free-solid-svg-icons";

export default function ListServices() {
  const services = useSelector((state) => state.service.services);
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);

  const [currentServiceIndex, setCurrentServiceIndex] = useState(0);
  const [complementaryIndexes, setComplementaryIndexes] = useState({});
  const [realisationIndexes, setRealisationIndexes] = useState({});
  const [complementaryServiceId, setComplementaryServiceId] = useState(null);
  const [openRealisationServiceId, setOpenRealisationServiceId] = useState(null);
  const [openAddService, setOpenAddService] = useState(false);
  const [isRotatingMain, setIsRotatingMain] = useState(false);
  const [isRotatingComplementary, setIsRotatingComplementary] = useState(false);
  const [isRotatingRealisation, setIsRotatingRealisation] = useState(false);

  useEffect(() => {
    if (currentServiceIndex >= services.length) {
      setCurrentServiceIndex(Math.max(0, services.length - 1));
    }
  }, [services.length, currentServiceIndex]);

  const safeIndex = Math.min(currentServiceIndex, services.length - 1);
  const currentService = services[safeIndex];

  // ======== AJUSTEMENT DES INDEX POUR SERVICES COMPLEMENTAIRES ========
  useEffect(() => {
    if (!currentService) return;

    const complementaires = currentService.complementaires || [];
    const compIndex = complementaryIndexes[currentService.id] || 0;

    if (compIndex >= complementaires.length && complementaires.length > 0) {
      setComplementaryIndexes((prev) => ({
        ...prev,
        [currentService.id]: complementaires.length - 1,
      }));
    }
    if (complementaires.length === 0 && compIndex !== 0) {
      setComplementaryIndexes((prev) => ({
        ...prev,
        [currentService.id]: 0,
      }));
    }
  }, [currentService?.complementaires, complementaryIndexes, currentService]);

  // ======== AJUSTEMENT DES INDEX POUR REALISATIONS ========
  useEffect(() => {
    if (!currentService) return;

    const realisations = currentService.realisations || [];
    const realIndex = realisationIndexes[currentService.id] || 0;

    if (realIndex >= realisations.length && realisations.length > 0) {
      setRealisationIndexes((prev) => ({
        ...prev,
        [currentService.id]: realisations.length - 1,
      }));
    }
    if (realisations.length === 0 && realIndex !== 0) {
      setRealisationIndexes((prev) => ({
        ...prev,
        [currentService.id]: 0,
      }));
    }
  }, [currentService?.realisations, realisationIndexes, currentService]);

  if (!currentService) {
    return <div style={{ color: "#fff", textAlign: "center" }}>Aucun service à afficher</div>;
  }

  const complementaires = currentService.complementaires || [];
  const realisations = currentService.realisations || [];
  const compIdx = complementaryIndexes[currentService.id] || 0;
  const realIdx = realisationIndexes[currentService.id] || 0;

  const currentComplementaire = complementaires[compIdx];
  const currentRealisation = realisations[realIdx];

  const triggerRotation = (setter, callback) => {
    setter(true);
    setTimeout(() => {
      callback();
      setter(false);
    }, 500);
  };

  const handleAddPhotoClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handlePhotoChange = (e) => {
    const files = Array.from(e.target.files);
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result;
        const photo = {
          id: Date.now().toString() + Math.random(),
          name: file.name,
          base64,
        };
        dispatch(addPhoto(safeIndex, realIdx, photo));
      };
      reader.readAsDataURL(file);
    });
    e.target.value = null;
  };

  const handleDelete = (id) => {
    if (window.confirm("Êtes-vous sûr de vouloir supprimer ce service ?")) {
      dispatch(removeService(id));
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.card} className={isRotatingMain ? "rotate-animation" : ""}>
        <button style={styles.deleteButton} onClick={() => handleDelete(currentService.id)}>
          <FontAwesomeIcon icon={faTrash} />
        </button>

        <h3>{currentService.name}</h3>
        <p><strong>Activité principale:</strong> {currentService.activity?.name || "Non renseignée"}</p>
        <p><strong>Sous-activité:</strong> {currentService.subActivity?.name || "Non renseignée"}</p>
        <p>{currentService.description}</p>
        <p><strong>Tarif:</strong> {currentService.price} {currentService.pricingUnit}</p>

        {/* Services complémentaires */}
        <div style={styles.section} className={isRotatingComplementary ? "rotate-animation" : ""}>
          <h4>Services complémentaires</h4>
          {complementaires.length > 0 && currentComplementaire ? (
            <>
              <div style={styles.item}>
                <button style={styles.deleteButton} onClick={() => {
                  if (currentComplementaire) {
                    dispatch(removeServiceComplementaire(currentService.id, currentComplementaire.id));
                  }
                }}>
                  <FontAwesomeIcon icon={faTrash} />
                </button>
                <h5>{currentComplementaire?.name || "Nom non disponible"}</h5>
                <p>{currentComplementaire?.description || "Description non disponible"}</p>
                <p><strong>Tarif :</strong> {currentComplementaire?.price} {currentComplementaire?.pricingUnit}</p>
              </div>
              <CarouselControls
                onPrev={() => triggerRotation(setIsRotatingComplementary, () =>
                  setComplementaryIndexes((prev) => ({
                    ...prev,
                    [currentService.id]: compIdx === 0 ? complementaires.length - 1 : compIdx - 1,
                  }))
                )}
                onNext={() => triggerRotation(setIsRotatingComplementary, () =>
                  setComplementaryIndexes((prev) => ({
                    ...prev,
                    [currentService.id]: (compIdx + 1) % complementaires.length,
                  }))
                )}
                onAdd={() => setComplementaryServiceId(currentService.id)}
                isPrevDisabled={complementaires.length <= 1}
                isNextDisabled={complementaires.length <= 1}
              />
            </>
          ) : (
            <>
              <p>Aucun service complémentaire.</p>
              <CarouselControls
                onPrev={() => {}}
                onNext={() => {}}
                onAdd={() => setComplementaryServiceId(currentService.id)}
                isPrevDisabled={true}
                isNextDisabled={true}
              />
            </>
          )}
        </div>

        {/* Réalisations */}
        <div style={styles.section} className={isRotatingRealisation ? "rotate-animation" : ""}>
          <h4>Réalisations</h4>
          {realisations.length > 0 && currentRealisation ? (
            <>
              <div style={styles.item}>
                <button style={styles.deleteButton} onClick={() => {
                  if (currentRealisation) {
                    dispatch(removeRealisation(currentService.id, currentRealisation.id));
                  }
                }}>
                  <FontAwesomeIcon icon={faTrash} />
                </button>
                <h5>{currentRealisation?.title || "Titre non disponible"}</h5>
                <p>{currentRealisation?.description || "Description non disponible"}</p>
                {currentRealisation?.photos?.length > 0 && (
                  <div style={styles.photos}>
                    {currentRealisation.photos.map((photo, photoIndex) => (
                      <div key={photo.id} style={{ position: "relative" }}>
                        <img src={photo.base64} alt={photo.name || "Photo réalisation"} style={styles.photo} />
                        <button type="button" style={styles.deleteButton} onClick={() =>
                          dispatch(removePhoto(safeIndex, realIdx, photoIndex))
                        }>
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <button type="button" style={styles.addButton} onClick={handleAddPhotoClick}>
                  + Ajouter des photos
                </button>
                <input type="file" accept="image/*" style={{ display: "none" }} ref={fileInputRef} onChange={handlePhotoChange} multiple />
              </div>
              <CarouselControls
                onPrev={() => triggerRotation(setIsRotatingRealisation, () =>
                  setRealisationIndexes((prev) => ({
                    ...prev,
                    [currentService.id]: realIdx === 0 ? realisations.length - 1 : realIdx - 1,
                  }))
                )}
                onNext={() => triggerRotation(setIsRotatingRealisation, () =>
                  setRealisationIndexes((prev) => ({
                    ...prev,
                    [currentService.id]: (realIdx + 1) % realisations.length,
                  }))
                )}
                onAdd={() => setOpenRealisationServiceId(currentService.id)}
                isPrevDisabled={realisations.length <= 1}
                isNextDisabled={realisations.length <= 1}
              />
            </>
          ) : (
            <>
              <p>Aucune réalisation.</p>
              <CarouselControls
                onPrev={() => {}}
                onNext={() => {}}
                onAdd={() => setOpenRealisationServiceId(currentService.id)}
                isPrevDisabled={true}
                isNextDisabled={true}
              />
            </>
          )}
        </div>
      </div>

      <CarouselControls
        onPrev={() =>
          triggerRotation(setIsRotatingMain, () =>
            setCurrentServiceIndex((prev) =>
              prev === 0 ? services.length - 1 : prev - 1
            )
          )
        }
        onNext={() =>
          triggerRotation(setIsRotatingMain, () =>
            setCurrentServiceIndex((prev) => (prev + 1) % services.length)
          )
        }
        onAdd={() => setOpenAddService(true)}
        isPrevDisabled={services.length <= 1}
        isNextDisabled={services.length <= 1}
      />

      {complementaryServiceId === currentService.id && (
        <FormServiceModal
          parentServiceId={currentService.id}
          onClose={() => setComplementaryServiceId(null)}
        />
      )}

      {openAddService && (
        <FormServiceModal onClose={() => setOpenAddService(false)} />
      )}

      {openRealisationServiceId === currentService.id && (
        <FormRealisationModal
          serviceIndex={safeIndex}
          onClose={() => setOpenRealisationServiceId(null)}
        />
      )}
    </div>
  );
}

const styles = {
  container: {
    marginTop: "1rem",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  card: {
    backgroundColor: "#3b0a29",
    color: "#f5f5f5",
    padding: "1rem",
    borderRadius: "8px",
    width: "90%",
    position: "relative",
  },
  section: {
    margin: "auto",
    marginTop: "1rem",
    border: "1px solid #9a4d72",
    backgroundColor: "#531b3f",
    color: "#f5f5f5",
    width: "90%",
    padding: "0.5rem",
  },
  item: {
    margin: "auto",
    marginTop: "0.5rem",
    border: "1px solid #9a4d72",
    backgroundColor: "#6f264f",
    color: "#f5f5f5",
    width: "70%",
    padding: "0.5rem",
    position: "relative",
  },
  photos: {
    display: "flex",
    gap: "0.5rem",
    flexWrap: "wrap",
    width: "fit-content",
    margin: "auto",
    marginTop: "0.5rem",
  },
  photo: {
    width: "100px",
    height: "100px",
    objectFit: "contain",
    borderRadius: "4px",
    backgroundColor: "black",
  },
  deleteButton: {
    position: "absolute",
    top: 5,
    right: 5,
    background: "rgba(255,0,0,0.7)",
    border: "none",
    color: "white",
    borderRadius: "50%",
    width: 24,
    height: 24,
    cursor: "pointer",
  },
  addButton: {
    marginTop: "1rem",
  },
};
